import { formatPrice } from "utils/convertNumbThousand";

// Company-level price-display toggles, served on the company configuration
// response (`/api/company/configurations/`) and read from `ourCompany` in
// DataContext. Optional because older config rows / in-flight loads may omit
// them; defaults below preserve the historical "per-day only" behaviour.
export interface CompanyPriceDisplayFlags {
  show_per_day_price?: boolean;
  show_per_week_price?: boolean;
  show_per_month_price?: boolean;
}

export type PricePeriod = "day" | "week" | "month";

export interface PeriodPrice {
  period: PricePeriod;
  label: string; // "/day" | "/week" | "/month"
  amount: number; // computed price for the period
  formatted: string; // e.g. "$1,500.00"
}

// The Fleet model only stores a daily rate, so weekly/monthly prices are
// derived from it: week = daily x 7, month = daily x 30. These are indicative
// "from" prices for the listings; the actual multi-day total (with any
// configured discounts) is still computed by the backend at checkout.
export const PERIOD_DAYS: Record<PricePeriod, number> = {
  day: 1,
  week: 7,
  month: 30,
};

const PERIOD_LABEL: Record<PricePeriod, string> = {
  day: "/day",
  week: "/week",
  month: "/month",
};

/**
 * Resolve which price periods to display for a vehicle, given the company's
 * display toggles, in day -> week -> month order.
 *
 * - Per-day defaults to ON (preserves current behaviour); week/month default OFF.
 * - If an operator disables every toggle we still fall back to per-day, so a
 *   vehicle card never renders without a price.
 */
export function getEnabledPeriodPrices(
  perDay: number | null | undefined,
  flags?: CompanyPriceDisplayFlags | null,
): PeriodPrice[] {
  const base = Number(perDay ?? 0);

  const enabled: PricePeriod[] = [];
  if (flags?.show_per_day_price ?? true) enabled.push("day");
  if (flags?.show_per_week_price) enabled.push("week");
  if (flags?.show_per_month_price) enabled.push("month");
  if (enabled.length === 0) enabled.push("day");

  return enabled.map((period) => {
    const amount = base * PERIOD_DAYS[period];
    return {
      period,
      label: PERIOD_LABEL[period],
      amount,
      formatted: `$${formatPrice(amount)}`,
    };
  });
}
